'use client';

import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
  invalid?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, invalid = false, className, ...rest },
  ref,
) {
  const localRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (localRef.current) localRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  function setRef(el: HTMLInputElement | null) {
    (localRef as { current: HTMLInputElement | null }).current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  }

  const classes = [
    'h-4 w-4 rounded-control border accent-[color:var(--color-selected)]', // checked = blue selection (ADR-0114)
    invalid ? 'border-danger' : 'border-border',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
    'disabled:opacity-disabled disabled:cursor-not-allowed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <input
      type="checkbox"
      ref={setRef}
      aria-invalid={invalid || undefined}
      className={classes}
      {...rest}
    />
  );
});
